import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Send, Loader2 } from "lucide-react";
import { Reveal } from "../lib/motion";
import { BRAND, SERVICE_OPTIONS } from "../lib/data";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;
const empty = { name: "", email: "", phone: "", vehicle_make: "", vehicle_model: "", vehicle_year: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildWhatsApp = () => {
    const text = `Hallo ECU SOLUTIONS,%0A%0AIch interessiere mich für: ${form.service || "Beratung"}%0AFahrzeug: ${form.vehicle_make} ${form.vehicle_model} ${form.vehicle_year}%0AName: ${form.name}%0A%0A${form.message}`;
    return `https://wa.me/${BRAND.whatsapp}?text=${text}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.vehicle_make || !form.vehicle_model || !form.service) {
      toast.error("Bitte Name, E-Mail, Fahrzeug und Service ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Anfrage gesendet! Wir melden uns schnellstmöglich.");
      setForm(empty);
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte versuch es später erneut oder schreib uns per WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-brand-dark border-slate-800 text-white rounded-none h-12 focus-visible:ring-brand focus-visible:border-brand placeholder:text-slate-600";

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-28 md:py-36 bg-brand-dark">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-brand" /> Kontakt
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white leading-[1.05]">
              Starte deine <span className="text-brand">Anfrage.</span>
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed max-w-md">
              Sag uns, welches Fahrzeug du fährst und was du dir wünschst. Wir prüfen die
              Möglichkeiten kostenlos und melden uns mit einer ehrlichen Einschätzung.
            </p>

            <div className="mt-10 space-y-4">
              <a href={buildWhatsApp()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
                 className="flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-4 px-6 transition-colors uppercase tracking-wider text-sm w-full sm:w-auto">
                <MessageCircle size={20} /> Per WhatsApp anfragen
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link"
                 className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                <Mail size={18} className="text-brand" />{BRAND.email}
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="bg-brand-surface border border-slate-800 p-6 sm:p-10 space-y-6">
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Marke *</Label>
                  <Input data-testid="input-make" value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="z.B. BMW" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Modell *</Label>
                  <Input data-testid="input-model" value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="z.B. 335i" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Baujahr</Label>
                  <Input data-testid="input-year" value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2018" className={`mt-2 ${inputCls}`} />
                </div>
              </div>

              <div>
                <Label htmlFor="contact-service" className="font-mono text-[11px] uppercase tracking-wider text-white/50">Gewünschter Service *</Label>
                <select id="contact-service" data-testid="select-service" value={form.service} onChange={set("service")}
                  className={`mt-2 w-full min-w-0 border px-3 text-sm ${inputCls}`}>
                  <option value="" disabled>Service wählen</option>
                  {SERVICE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Name *</Label>
                  <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Dein Name" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">E-Mail *</Label>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="du@mail.de" className={`mt-2 ${inputCls}`} />
                </div>
              </div>

              <div>
                <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Telefon</Label>
                <Input data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="Optional" className={`mt-2 ${inputCls}`} />
              </div>

              <div>
                <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Nachricht</Label>
                <Textarea data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Erzähl uns mehr über dein Projekt..."
                  className="mt-2 bg-brand-dark border-slate-800 text-white rounded-none min-h-[120px] focus-visible:ring-brand focus-visible:border-brand placeholder:text-slate-600" />
              </div>

              <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                className="flex items-center justify-center gap-2 w-full bg-brand hover:bg-brand-light disabled:opacity-60 text-white font-semibold py-4 px-8 border border-brand-light/40 transition-colors hover:shadow-[0_0_24px_rgba(0,85,255,0.45)] uppercase tracking-wider text-sm">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {loading ? "Wird gesendet..." : "Anfrage absenden"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
